/**
 * Delivery error/reason formatting, ported from Ember's
 * `app/utils/errors/format-error.ts` + its channel formatters.
 *
 * A raw delivery error is often a provider code (`NoDevicesSynced`,
 * `APNS_BadDeviceToken`, `twilio error 30006 …`) that must be rewritten to
 * human copy. Ember dispatches on the delivery's channel (`actionTypeName`):
 * push and SMS/twilio have channel-specific maps; every other channel falls
 * back to the raw detail. We port the two channels that actually reach a
 * delivery's Reason cell — `push` (message map, state-aware, and a multi-device
 * JSON payload reduced to a single error or a device-count summary) and `twilio`
 * (error-code regex) — and fall back to the raw detail otherwise, matching
 * Ember's helper `try/catch` behaviour for unknown channels.
 *
 * SCOPE: Ember's `api`/`stripe` formatters are billing-error maps, not delivery
 * channels, so they're intentionally not ported here. Embedded hyperlinks in a
 * few push messages (and Twilio's error-doc link) are flattened to plain text —
 * the Reason cell renders plain text, so a real anchor is a later enhancement.
 */

import { isEmptyString } from '@/utils/string';

/** The channels with a dedicated formatter — derived from a delivery's `action_type`. */
export type FormatterType = 'push' | 'twilio';

const isKnownFormatter = (type: string): type is FormatterType => type === 'push' || type === 'twilio';

/** Maps a delivery's `action_type` (e.g. `push_action`) to a formatter channel key. */
export function channelFormatterType(actionType: string): FormatterType | undefined {
  const channel = actionType.replace(/_action$/, '');
  return isKnownFormatter(channel) ? channel : undefined;
}

// --- Push -----------------------------------------------------------------

/**
 * A push error's copy: one line, a line per delivery state, or — where the
 * provider explains the rejection itself — a line built from the error's `meta`.
 */
type PushMessage = string | Record<string, string> | ((providerMessage: string | null) => string);
type PushErrorEntry = { code: string; message: PushMessage };
/** A plain sentence (no code prefix) built from what the provider stored about the device. */
type StoredMessageSentence = (storedMessage: string | null) => string;
type PushError = string | PushErrorEntry | StoredMessageSentence;

/** One device's entry in a multi-device push payload (`meta.reason` JSON). */
type DeviceError = Record<string, unknown>;

/**
 * The provider's own sentence about a rejection, when it sent one.
 * Firebase puts it in `meta.full_error_message`.
 */
function providerErrorMessage(meta: unknown): string | null {
  if (meta == null || typeof meta !== 'object' || !('full_error_message' in meta)) {
    return null;
  }
  const message = meta.full_error_message;
  return typeof message === 'string' && !isEmptyString(message) ? message : null;
}

/**
 * What the provider said about one device. Native push reads `meta.full_error_message`,
 * as it always has. A web push failure (`WEBPUSH_*`) is stored top-level on the device
 * entry (`PushStat.full_error_message`), so only those codes read it there.
 */
function deviceProviderMessage(device: DeviceError): string | null {
  const nested = providerErrorMessage(device.meta);
  if (nested != null || !isWebPushErrorCode(device.detail)) return nested;
  return providerErrorMessage(device);
}

function isWebPushErrorCode(detail: unknown): boolean {
  return typeof detail === 'string' && detail.startsWith('WEBPUSH_');
}

const pushError = (provider: string | null, code: string, message: PushMessage): PushErrorEntry => ({
  code: provider != null ? `${provider} ${code}` : code,
  message,
});

// --- Web push --------------------------------------------------------------

const UNDEFINED_VARIABLE = /undefined variable:? "?([\w.\-[\]]+)/g;

/** Every `undefined variable: <name>` in a Liquid error, unique, in order. */
function undefinedVariables(storedMessage: string | null): string[] {
  const names = [...(storedMessage ?? '').matchAll(UNDEFINED_VARIABLE)].map((match) => match[1] ?? '');
  return [...new Set(names.filter((name) => !isEmptyString(name)))];
}

/** "a", "a and b", "a, b, and c". */
function oxfordList(items: string[]): string {
  if (items.length <= 2) return items.join(' and ');
  return `${items.slice(0, -1).join(', ')}, and ${items.at(-1) ?? ''}`;
}

/** Why the variables were missing, by where they all come from. */
function missingVariablesClause(names: string[]): string {
  if (names.every((name) => name.startsWith('event.'))) return "which this event didn't include";
  if (names.every((name) => name.startsWith('customer.'))) return "which this person's profile doesn't have";
  return names.length === 1 ? "which wasn't available when it was sent" : "which weren't available when it was sent";
}

function webPushRenderFailed(storedMessage: string | null): string {
  const names = undefinedVariables(storedMessage);
  if (names.length === 0) return "Not sent: the message's Liquid didn't render.";
  const variables = oxfordList(names.map((name) => `{{${name}}}`));
  return `Not sent: the message uses ${variables}, ${missingVariablesClause(names)}.`;
}

const PUSH_SERVICE_REASON = /"reason"\s*:\s*"([^"]+)"/;

function webPushInvalidRequest(storedMessage: string | null): string {
  const reason = PUSH_SERVICE_REASON.exec(storedMessage ?? '')?.[1];
  if (reason === 'BadWebPushTopic') {
    return "Apple's push service rejected how this message was addressed. Nothing in your message caused it.";
  }
  return `The browser's push service rejected the notification${reason == null ? '' : ` (${reason})`}.`;
}

/**
 * Apple answers `BadJwtToken` for any VAPID JWT it can't verify: a subject on a domain Safari rejects, but also a
 * signing-key, audience or expiry problem. So it names no cause; the settings page warns about the saved subject
 * when that is the one Safari rejects.
 */
function webPushVapidAuthFailed(storedMessage: string | null): string {
  if (storedMessage?.includes('BadJwtToken') === true) {
    return "Safari couldn't verify the web push authorization (BadJwtToken).";
  }
  return "The browser's push service rejected your web push keys or contact address.";
}

// Plain sentences, no code prefix: the code shows beside the stored error instead.
const WEB_PUSH_ERRORS = {
  WEBPUSH_RENDER_FAILED: webPushRenderFailed,
  WEBPUSH_UNREGISTERED: 'The browser unsubscribed, so the device was removed.',
  // Only an unsubscribed browser is removed; these stay on the profile, failing, until it subscribes again.
  WEBPUSH_INVALID_SUBSCRIPTION:
    'The browser subscription is invalid. The device stays on the profile and gets nothing until it subscribes again.',
  WEBPUSH_VAPID_REJECTED:
    'The browser subscribed with a different web push key. The device stays on the profile and gets nothing until it subscribes again.',
  WEBPUSH_VAPID_AUTH_FAILED: webPushVapidAuthFailed,
  WEBPUSH_INVALID_REQUEST: webPushInvalidRequest,
  WEBPUSH_PAYLOAD_TOO_LARGE: 'The notification is too large for browsers. Shorten the text, links or buttons.',
  WEBPUSH_RETRYABLE: "The browser's push service didn't respond. We'll retry.",
  WEBPUSH_MISSING_KEYS: "This browser subscription has no encryption keys, so it can't receive web push.",
  WEBPUSH_CUSTOM_PAYLOAD_UNSUPPORTED:
    'Not sent to browsers: web push needs the rich editor, and custom JSON payloads have no web content.',
} satisfies Record<string, PushError>;

// Ported verbatim from Ember `app/utils/errors/messages/push.js`. Two FCM
// entries carry an anchor in Ember; flattened to plain text here.
const PUSH_ERRORS: Record<string, PushError> = {
  NoDevicesSynced: 'No devices synced',
  NoMatchingDevicesSynced: 'No devices synced for the selected app',
  NoDevice: 'No device found',
  NoProviderConfigured: 'No provider configured for this platform',
  ProviderTokenError: 'Could not generate auth token for provider using supplied credentials',
  EMPTY_NOTIFICATION_BODY:
    'The notification body contained no content. Check the message payload and any Liquid in it.',
  PushInvalidKey: pushError(null, 'Invalid key', 'Check that this is the correct JSON key and try again.'),
  PushInvalidCertificate: pushError(
    null,
    'Invalid certificate',
    'Check that this is the correct .p8 file and try again.',
  ),
  APNS_BadCollapseId: pushError('APNs', '400 BadCollapseId', 'The apns-collapse-id in your custom payload is invalid'),
  APNS_BadDeviceToken: pushError(
    'APNs',
    '400 BadDeviceToken',
    "Device token is invalid or doesn't work with this platform. Register a corrected token.",
  ),
  APNS_BadExpirationDate: pushError(
    'APNs',
    '400 BadExpirationDate',
    'The apns-expiration value in your custom payload is invalid.',
  ),
  APNS_BadMessageId: pushError('APNs', '400 BadMessageId', 'The apns-id value in your custom payload is invalid.'),
  APNS_BadPriority: pushError('APNs', '400 BadPriority', 'The apns-priority value in your custom payload is invalid.'),
  APNS_BadTopic: pushError('APNs', '400 BadTopic', 'Your bundle ID is invalid. Check your configuration.'),
  APNS_DeviceTokenNotForTopic: pushError(
    'APNs',
    '400 DeviceTokenNotForTopic',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_DuplicateHeaders: pushError(
    'APNs',
    '400 DuplicateHeaders',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_IdleTimeout: pushError('APNs', '400 IdleTimeout', {
    default: 'Internal Customer.io error. Report this to win@customer.io.',
    delivery: 'Internal Customer.io error. Report this to win@customer.io.',
  }),
  APNS_MissingDeviceToken: pushError(
    'APNs',
    '400 MissingDeviceToken',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_MissingTopic: pushError(
    'APNs',
    '400 MissingTopic',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_PayloadEmpty: pushError('APNs', '400 PayloadEmpty', 'The message payload is empty.'),
  APNS_TopicDisallowed: pushError(
    'APNs',
    '400 TopicDisallowed',
    'Device token is not registered with the current bundle ID. Check your configuration or register a corrected token.',
  ),
  APNS_BadCertificate: pushError(
    'APNs',
    '403 BadCertificate',
    'Your .p8 certificate is invalid. Check your configuration.',
  ),
  APNS_BadCertificateEnvironment: pushError(
    'APNs',
    '403 BadCertificateEnvironment',
    'Check that your .p8 certificate is for the correct APNs environment.',
  ),
  APNS_ExpiredProviderToken: pushError(
    'APNs',
    '403 ExpiredProviderToken',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_Forbidden: pushError('APNs', '403 Forbidden', 'Internal Customer.io error. Report this to win@customer.io.'),
  APNS_InvalidProviderToken: pushError(
    'APNs',
    '403 InvalidProviderToken',
    'Invalid APNS provider token. Reset the iOS push credentials by disabling and re-enabling push in your workspace settings.',
  ),
  APNS_MissingProviderToken: pushError(
    'APNs',
    '403 MissingProviderToken',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_BadPath: pushError('APNs', '404 BadPath', 'Internal Customer.io error. Report this to win@customer.io.'),
  APNS_MethodNotAllowed: pushError(
    'APNs',
    '405 MethodNotAllowed',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  APNS_Unregistered: pushError('APNs', '410 Unregistered', 'This user has uninstalled the application.'),
  APNS_PayloadTooLarge: pushError(
    'APNs',
    '413 PayloadTooLarge',
    'Your message payload was too large. The limit is 4KB.',
  ),
  APNS_TooManyProviderTokenUpdates: pushError('APNs', '429 TooManyProviderTokenUpdates', {
    default: 'Internal Customer.io error. Report this to win@customer.io.',
    delivery: 'Internal Customer.io error. Report this to win@customer.io.',
  }),
  APNS_TooManyRequests: pushError('APNs', '429 TooManyRequests', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  APNS_InternalServerError: pushError('APNs', '500 InternalServerError', {
    default: 'Internal Customer.io error. We will retry; check back soon.',
    delivery: 'Internal Customer.io error. We will retry; check back soon.',
  }),
  APNS_ServiceUnavailable: pushError('APNs', '503 ServiceUnavailable', {
    default: 'Internal Customer.io error. We will retry; check back soon.',
    delivery: 'Internal Customer.io error. We will retry; check back soon.',
  }),
  APNS_Shutdown: pushError('APNs', '503 Shutdown', {
    default: 'Internal Customer.io error. We will retry; check back soon.',
    delivery: 'Internal Customer.io error. We will retry; check back soon.',
  }),
  FCM_UNSPECIFIED_ERROR: pushError(
    'FCM',
    'UNSPECIFIED_ERROR',
    'Internal Customer.io error. Report this to win@customer.io.',
  ),
  // Firebase rejects a payload with its own sentence about what was wrong, which
  // says far more than the fixed line; the fixed line is the fallback when the
  // response carried none.
  FCM_INVALID_ARGUMENT: pushError('FCM', 'INVALID_ARGUMENT', (providerMessage) =>
    providerMessage == null
      ? 'Part of your notification was invalid. Report this error to win@customer.io for more detail.'
      : `${providerMessage} Learn more about error codes for FCM failure conditions.`,
  ),
  FCM_UNREGISTERED: pushError('FCM', 'UNREGISTERED', 'This user has uninstalled the application.'),
  FCM_SENDER_ID_MISMATCH: pushError(
    'FCM',
    'SENDER_ID_MISMATCH',
    'The device token is associated with a different project than your Firebase certificate. Register a corrected token or replace the certificate.',
  ),
  FCM_QUOTA_EXCEEDED: pushError('FCM', 'QUOTA_EXCEEDED', {
    default: 'Rate limit for device exceeded. We will retry; check back soon.',
    delivery: 'Rate limit for device exceeded. We will retry; check back soon.',
  }),
  FCM_APNS_AUTH_ERROR: pushError(
    'FCM',
    'APNS_AUTH_ERROR',
    'Check that your FCM iOS certificate matches the one in Customer.io.',
  ),
  FCM_UNAVAILABLE: pushError('FCM', 'UNAVAILABLE', {
    default: 'Internal Customer.io error. We will retry; check back soon.',
    delivery: 'Internal Customer.io error. We will retry; check back soon.',
  }),
  FCM_INTERNAL: pushError('FCM', 'INTERNAL', {
    default: 'Internal Customer.io error. We will retry; check back soon.',
    delivery: 'Internal Customer.io error. We will retry; check back soon.',
  }),
  FCM_INVALID_TOKEN: pushError(
    'FCM',
    'INVALID_TOKEN',
    'Invalid platform or token. Retry with another token or platform.',
  ),
  FCM_PERMISSION_DENIED: pushError(
    'FCM',
    'PERMISSION_DENIED',
    'Make sure the Firebase Cloud Messaging API is enabled in your Firebase config.',
  ),
  ...WEB_PUSH_ERRORS,
};

function pushEntryToString(
  entry: PushErrorEntry,
  deliveryState: string | null,
  providerMessage: string | null,
): string {
  const { message } = entry;
  if (typeof message === 'function') {
    return `${entry.code}: ${message(providerMessage)}`;
  }
  if (typeof message === 'string') {
    return `${entry.code}: ${message}`;
  }
  // Variant-keyed message; the Reason cell never passes a variant, so use the
  // delivery state's copy when present, else the `default`.
  const variant = deliveryState != null && !isEmptyString(message[deliveryState]) ? deliveryState : 'default';
  return `${entry.code}: ${message[variant] ?? message.default}`;
}

/** The lookup key for a push error is the raw string, or a device error's `detail`. */
function pushErrorKey(error: string | DeviceError): string {
  if (typeof error === 'string') {
    return error;
  }
  const { detail } = error;
  return typeof detail === 'string' ? detail : '';
}

function formatSinglePushError(
  error: string | DeviceError,
  deliveryState: string | null,
  providerMessage: string | null,
): string {
  const key = pushErrorKey(error);
  const configured = PUSH_ERRORS[key];
  if (configured == null) {
    return key;
  }
  if (typeof configured === 'function') {
    return configured(providerMessage);
  }
  return typeof configured === 'string' ? configured : pushEntryToString(configured, deliveryState, providerMessage);
}

/** A push `meta.reason` may be a JSON array of per-device statuses. */
function parseDeviceErrors(reason: string): DeviceError[] | null {
  try {
    const parsed: unknown = JSON.parse(reason);
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return Array.isArray(parsed) ? (parsed as DeviceError[]) : null;
  } catch {
    return null;
  }
}

// Reduce a multi-device payload to a single error when possible — a lone entry,
// or the single entry matching the parent delivery state — else the caller shows
// a device-count summary. Ports Ember's `reduceToSingleErrorIfPossible`.
function reduceToSingleError(errors: DeviceError[], deliveryState: string | null): DeviceError | undefined {
  if (errors.length === 1) {
    return errors[0];
  }
  const matching = errors.filter((error) => {
    if (deliveryState === 'failed') {
      return error.status !== 200;
    }
    if (deliveryState == null) {
      return false;
    }
    return Boolean(error[deliveryState]);
  });
  return matching.length === 1 ? matching[0] : undefined;
}

function pushFormatter(reason: string, deliveryState: string | null, providerMessage: string | null): string {
  const devices = parseDeviceErrors(reason);
  if (devices != null) {
    const single = reduceToSingleError(devices, deliveryState);
    if (single != null) {
      // A device's own entry carries whatever the provider said about that device.
      return formatSinglePushError(single, deliveryState, deviceProviderMessage(single));
    }
    return `${devices.length} devices ${deliveryState ?? ''}. See the Delivery Details page for more info.`;
  }
  return formatSinglePushError(reason, deliveryState, providerMessage);
}

// --- Twilio ---------------------------------------------------------------

// Twilio surfaces an error code either inline (immediate send failure) or via a
// later bounce notification. Ember linked the code to Twilio's docs; here the
// readable code line stays plain text (the anchor is a later enhancement).
const TWILIO_ERROR_PATTERNS = [
  // Twilio refused the delivery when it was sent.
  /(twilio error )(\d+)(.*)/i,
  // Twilio reported a failure later.
  /(twilio error code: )(\d+)(.*)/i,
];

/** A Twilio error split around its code: the text before it, the code, and the text after. */
export type TwilioErrorParts = { prefix: string; code: string; suffix: string };

/**
 * The parts of a Twilio error, or null when the text carries no Twilio error
 * code. Text before "twilio error" is not part of the result.
 */
export function parseTwilioError(text: string): TwilioErrorParts | null {
  for (const pattern of TWILIO_ERROR_PATTERNS) {
    const match = pattern.exec(text);
    if (match != null) {
      const [, prefix = '', code = '', suffix = ''] = match;
      return { prefix, code, suffix };
    }
  }
  return null;
}

/** Twilio's page on one of its error codes. */
export function twilioErrorDocsUrl(code: string): string {
  return `https://www.twilio.com/docs/api/errors/${code}`;
}

function twilioFormatter(errorKey: string): string {
  const parts = parseTwilioError(errorKey);
  return parts == null ? errorKey : `${parts.prefix}${parts.code}${parts.suffix}`;
}

// --- Dispatch -------------------------------------------------------------

/** Options mirroring Ember's `formatError(error, type, variant, deliveryState)`. */
export interface FormatErrorOptions {
  /** Formatter channel — from `channelFormatterType(action_type)`. */
  type?: FormatterType | null;
  /** Parent delivery state, used by the push variant map. */
  deliveryState?: string | null;
}

/** Pull the raw human string out of an error value for the fallback path. */
function extractDetail(error: string | Record<string, unknown> | null | undefined): string {
  if (typeof error === 'string') {
    return error;
  }
  if (error != null && typeof error === 'object' && 'detail' in error) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const { detail } = error as { detail: unknown };
    return typeof detail === 'string' ? detail : 'Unknown error';
  }
  return 'Unknown error';
}

/**
 * Resolve a delivery error/reason to human copy. Dispatches to the channel
 * formatter named by `type`; falls back to the raw detail for unknown channels
 * (email/webhook/slack/etc.), matching Ember's ungated helper.
 */
export function formatError(
  error: string | Record<string, unknown> | null | undefined,
  { type = null, deliveryState = null }: FormatErrorOptions = {},
): string {
  const detail = extractDetail(error);
  if (type == null || !isKnownFormatter(type)) {
    return detail;
  }
  if (type === 'twilio') {
    return twilioFormatter(detail);
  }
  const providerMessage = error == null || typeof error === 'string' ? null : deviceProviderMessage(error);
  return pushFormatter(detail, deliveryState, providerMessage);
}

/** A web push failure's code and the provider's message about it, verbatim, for display under the sentence. */
export type StoredPushError = { code: string; message: string | null };

/**
 * The stored failure behind a web push reason — a device entry, or a `meta.reason`
 * reduced to its single device the way `formatError` reduces it. Only `WEBPUSH_*`
 * codes have one, so every other push code keeps its sentence alone.
 */
export function storedPushError(
  error: string | DeviceError | null | undefined,
  deliveryState: string | null = null,
): StoredPushError | null {
  const device = typeof error === 'string' ? singleDeviceError(error, deliveryState) : error;
  if (device == null) return null;
  const code = pushErrorKey(device);
  return code.startsWith('WEBPUSH_') ? { code, message: deviceProviderMessage(device) } : null;
}

function singleDeviceError(reason: string, deliveryState: string | null): DeviceError | undefined {
  const devices = parseDeviceErrors(reason);
  return devices == null ? undefined : reduceToSingleError(devices, deliveryState);
}
